import { Box, BoxProps, IconButton } from '@mui/material';
import ArrowBackOutlinedIcon from '@mui/icons-material/ArrowBackOutlined';
import React, { FC, PropsWithChildren, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { BhabiWordmark } from './bhabi-wordmark';
import { Footer } from './footer';
import { useLandscapeMode } from '../hooks/use-Landscape';
import { usePublishHeight } from './ios-screen/hooks';
import '@/theme/ios-screen.css';

export { BhabiWordmark };

/**
 * Shared top bar frame. The first child (logo / back button) is the only
 * flexible cell — every other slot sizes to its content, so conditional
 * children (LeaveRoom, InviteNotifier…) never cause layout shift.
 */
export const TopBarLayout: FC<
  PropsWithChildren<{ outerBoxProps?: BoxProps }>
> = ({ children, outerBoxProps }) => {
  const isLandscape = useLandscapeMode();
  const headerRef = useRef<HTMLElement>(null);
  // Bounded screens size themselves as `100dvh - var(--bh-topbar-h)`, so the
  // real height (incl. the notch padding) is published instead of guessed.
  usePublishHeight(headerRef, '--bh-topbar-h');

  return (
    <Box
      component="header"
      ref={headerRef}
      {...outerBoxProps}
      sx={{ flexGrow: 1, position: 'relative', ...outerBoxProps?.sx }}
    >
      <Box
        className="bh-topbar"
        sx={{
          paddingTop: !isLandscape ? 'env(safe-area-inset-top)' : undefined,
        }}
      >
        <nav className="bh-topbar__inner" aria-label="Primary">
          {children}
        </nav>
      </Box>
    </Box>
  );
};

/**
 * Public pages (rules, terms): one bounded screen — back + wordmark top bar,
 * the page (which fills the rest) and an inline (not fixed) footer row.
 */
export const UnAuthLayout: FC<PropsWithChildren> = ({ children }) => {
  const nav = useNavigate();
  return (
    <div className="bh-frame">
      <TopBarLayout>
        <Box display="flex" alignItems="center" gap={0.5}>
          <IconButton
            aria-label="Back to home"
            sx={{ width: 44, height: 44 }}
            onClick={() => {
              nav('/');
            }}
          >
            <ArrowBackOutlinedIcon />
          </IconButton>
          <BhabiWordmark />
        </Box>
      </TopBarLayout>
      <div className="bh-frame__body">{children}</div>
      <Footer />
    </div>
  );
};
